// pages/admin/Notifications.tsx - COMPLETE FIXED VERSION
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import {
  fetchNotifications,
  createNotification,
  updateNotification,
  deleteNotification,
  fetchNotificationStatistics,
  fetchUsers,
} from "../../store/slices/adminSlice";
import {
  BellIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  GlobeAltIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CurrencyDollarIcon,
  UsersIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { User, Notification } from "../../types";

const AdminNotifications: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    notifications = [],
    users = [],
    isLoading,
  } = useSelector((state: RootState) => state.admin);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "system" as
      | "system"
      | "revenue"
      | "performance"
      | "payment"
      | "maintenance"
      | "alert",
    priority: "medium" as "low" | "medium" | "high" | "critical",
    is_global: false,
    is_active: true,
    user_ids: [] as number[],
    expires_at: "",
    amount: "",
  });

  useEffect(() => {
    dispatch(fetchNotifications({}));
    dispatch(fetchNotificationStatistics());
    dispatch(fetchUsers({}));
  }, [dispatch]);

  useEffect(() => {
    if (formData.is_global) {
      setSelectedUsers([]);
      setFormData((prev) => ({ ...prev, user_ids: [] }));
    }
  }, [formData.is_global]);

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.message.trim()) {
      alert("Please fill in all required fields");
      return;
    }

    if (!formData.is_global && selectedUsers.length === 0) {
      alert("Please select at least one user or make it a global notification");
      return;
    }

    try {
      const { amount, ...rest } = formData;
      const payload = {
        ...rest,
        user_ids: selectedUsers.map((user) => user.id),
        metadata:
          amount.trim() !== "" && !isNaN(Number(amount))
            ? { amount: Number(amount) }
            : undefined,
      };

      if (editingId) {
        await dispatch(
          updateNotification({ id: editingId, data: payload }),
        ).unwrap();
        setEditingId(null);
      } else {
        await dispatch(createNotification(payload)).unwrap();
      }

      setShowCreateModal(false);
      resetForm();
      // Refresh the notifications list
      dispatch(fetchNotifications({}));
    } catch (error) {
      console.error("Failed to save notification:", error);
      alert("Failed to save notification. Please try again.");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      message: "",
      type: "system",
      priority: "medium",
      is_global: false,
      is_active: true,
      user_ids: [],
      expires_at: "",
      amount: "",
    });
    setSelectedUsers([]);
    setEditingId(null);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this notification?")) {
      try {
        await dispatch(deleteNotification(id)).unwrap();
        // Refresh the notifications list
        dispatch(fetchNotifications({}));
      } catch (error) {
        console.error("Failed to delete notification:", error);
        alert("Failed to delete notification. Please try again.");
      }
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "alert":
        return ExclamationTriangleIcon;
      case "revenue":
        return CurrencyDollarIcon;
      case "system":
        return InformationCircleIcon;
      case "performance":
        return UsersIcon;
      case "payment":
        return CurrencyDollarIcon;
      case "maintenance":
        return ExclamationTriangleIcon;
      default:
        return BellIcon;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800 border border-red-200";
      case "critical":
        return "bg-purple-100 text-purple-800 border border-purple-200";
      case "low":
        return "bg-gray-100 text-gray-800 border border-gray-200";
      default:
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "alert":
        return "bg-red-50 text-red-700";
      case "revenue":
        return "bg-green-50 text-green-700";
      case "system":
        return "bg-blue-50 text-blue-700";
      case "performance":
        return "bg-purple-50 text-purple-700";
      case "payment":
        return "bg-yellow-50 text-yellow-700";
      case "maintenance":
        return "bg-orange-50 text-orange-700";
      default:
        return "bg-gray-50 text-gray-700";
    }
  };

  const handleUserSelect = (user: User) => {
    if (selectedUsers.find((u) => u.id === user.id)) {
      setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Notification Management
          </h1>
          <p className="text-sm text-gray-600">
            Create and manage system notifications
          </p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg transition-colors font-medium"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Create Notification</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Notifications</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {notifications.length}
              </p>
            </div>
            <BellIcon className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {notifications.filter((n: Notification) => n.is_active).length}
              </p>
            </div>
            <CheckIcon className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Global</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {notifications.filter((n: Notification) => n.is_global).length}
              </p>
            </div>
            <GlobeAltIcon className="h-8 w-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">High Priority</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {
                  notifications.filter(
                    (n: Notification) =>
                      n.priority === "high" || n.priority === "critical",
                  ).length
                }
              </p>
            </div>
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Notifications Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                All Notifications
              </h3>
              <p className="text-sm text-gray-600">
                Manage notifications sent to users
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => dispatch(fetchNotifications({}))}
                className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">
              Loading notifications...
            </p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <BellIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Notifications
            </h3>
            <p className="text-gray-600">
              Create your first notification to get started
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Create Notification</span>
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {notifications.map((notification: Notification) => {
              const Icon = getTypeIcon(notification.type);
              return (
                <div
                  key={notification.id}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div
                        className={`h-12 w-12 ${getTypeColor(
                          notification.type,
                        )} rounded-xl flex items-center justify-center flex-shrink-0`}
                      >
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2 mb-2">
                          <h4 className="font-semibold text-gray-900 truncate">
                            {notification.title}
                          </h4>
                          <span
                            className={`text-xs px-2.5 py-1 rounded-full font-medium ${getPriorityColor(
                              notification.priority,
                            )}`}
                          >
                            {notification.priority.toUpperCase()}
                          </span>
                          <span
                            className={`text-xs px-2.5 py-1 rounded-full font-medium ${getTypeColor(
                              notification.type,
                            )}`}
                          >
                            {notification.type.toUpperCase()}
                          </span>
                          {notification.is_global && (
                            <span className="text-xs px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                              GLOBAL
                            </span>
                          )}
                          {notification.is_active ? (
                            <span className="text-xs px-2.5 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                              ACTIVE
                            </span>
                          ) : (
                            <span className="text-xs px-2.5 py-1 bg-gray-100 text-gray-800 rounded-full font-medium">
                              INACTIVE
                            </span>
                          )}
                          {notification.metadata?.amount !== undefined &&
                            notification.metadata?.amount !== null && (
                              <span
                                className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                                  Number(notification.metadata.amount) < 0
                                    ? "bg-red-100 text-red-800"
                                    : "bg-green-100 text-green-800"
                                }`}
                              >
                                {Number(notification.metadata.amount) < 0
                                  ? "-"
                                  : ""}
                                $
                                {Math.abs(
                                  Number(notification.metadata.amount),
                                ).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            )}
                        </div>
                        <p className="text-gray-600 mb-3">
                          {notification.message}
                        </p>
                        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center space-x-1">
                            <ClockIcon className="h-3 w-3" />
                            <span>
                              Sent: {formatDate(notification.sent_at)}
                            </span>
                            {notification.expires_at && (
                              <>
                                <span>•</span>
                                <span>
                                  Expires: {formatDate(notification.expires_at)}
                                </span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className="flex items-center space-x-1">
                              <EyeIcon className="h-3 w-3" />
                              <span>Read: {notification.read_count || 0}</span>
                            </span>
                            {notification.total_sent !== undefined && (
                              <span>Sent to: {notification.total_sent}</span>
                            )}
                            {notification.dismissed_count !== undefined && (
                              <span>
                                Dismissed: {notification.dismissed_count}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => {
                          setEditingId(notification.id);
                          setFormData({
                            title: notification.title,
                            message: notification.message,
                            type: notification.type,
                            priority: notification.priority,
                            is_global: notification.is_global,
                            is_active: notification.is_active,
                            user_ids: [],
                            expires_at: notification.expires_at || "",
                            amount:
                              notification.metadata?.amount !== undefined &&
                              notification.metadata?.amount !== null
                                ? String(notification.metadata.amount)
                                : "",
                          });
                          setShowCreateModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(notification.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {editingId
                      ? "Edit Notification"
                      : "Create New Notification"}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {editingId
                      ? "Update the notification details"
                      : "Create and send a new notification to users"}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingId(null);
                    resetForm();
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter notification title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          type: e.target.value as any,
                        })
                      }
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="system">System</option>
                      <option value="revenue">Revenue</option>
                      <option value="performance">Performance</option>
                      <option value="payment">Payment</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="alert">Alert</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-40"
                    placeholder="Enter notification message"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          priority: e.target.value as any,
                        })
                      }
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount (USD)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) =>
                        setFormData({ ...formData, amount: e.target.value })
                      }
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g. 8318.80 or -2083.47"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Optional. Shown to the user on their notifications page.
                      Use a negative value for deductions.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiration Date
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.expires_at}
                      onChange={(e) =>
                        setFormData({ ...formData, expires_at: e.target.value })
                      }
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="is_global"
                      checked={formData.is_global}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_global: e.target.checked,
                        })
                      }
                      className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500 mt-1"
                    />
                    <div>
                      <label
                        htmlFor="is_global"
                        className="text-sm font-medium text-gray-700"
                      >
                        Send to all users (global notification)
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        This notification will be sent to all active users in
                        the system
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_active: e.target.checked,
                        })
                      }
                      className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500 mt-1"
                    />
                    <div>
                      <label
                        htmlFor="is_active"
                        className="text-sm font-medium text-gray-700"
                      >
                        Active (immediately visible to users)
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Inactive notifications won't be shown to users
                      </p>
                    </div>
                  </div>
                </div>

                {/* User Selection */}
                {!formData.is_global && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Users
                        </label>
                        <p className="text-xs text-gray-500">
                          {selectedUsers.length} user(s) selected
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowUserSelector(true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors text-sm font-medium"
                      >
                        <UsersIcon className="h-4 w-4" />
                        <span>Select Users</span>
                      </button>
                    </div>

                    {selectedUsers.length > 0 && (
                      <div className="space-y-2">
                        {selectedUsers.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {user.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {user.email}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleUserSelect(user)}
                              className="p-1 text-gray-400 hover:text-red-600"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingId(null);
                    resetForm();
                  }}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  {editingId
                    ? "Update Notification"
                    : "Create & Send Notification"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Selector Modal */}
      {showUserSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Select Users
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Choose users to receive this notification
                  </p>
                </div>
                <button
                  onClick={() => setShowUserSelector(false)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search users by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* User List */}
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No users found</p>
                  </div>
                ) : (
                  filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedUsers.find((u) => u.id === user.id)
                          ? "bg-blue-50 border-blue-200"
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                      }`}
                      onClick={() => handleUserSelect(user)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-medium">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {user.name}
                          </p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span
                              className={`text-xs px-2 py-1 rounded-full ${
                                user.role === "admin"
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {user.role}
                            </span>
                            <span
                              className={`text-xs px-2 py-1 rounded-full ${
                                user.is_active === false
                                  ? "bg-red-100 text-red-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {user.is_active === false ? "Inactive" : "Active"}
                            </span>
                          </div>
                        </div>
                      </div>
                      {selectedUsers.find((u) => u.id === user.id) && (
                        <CheckIcon className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowUserSelector(false)}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowUserSelector(false);
                    setFormData((prev) => ({
                      ...prev,
                      user_ids: selectedUsers.map((user) => user.id),
                    }));
                  }}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Confirm Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
